import { Message, MessageStatus } from '@layerzerolabs/scan-client'

import { Address, PublicClient, createPublicClient, http, zeroAddress } from 'viem'
import { errorTopics, getOmniRemoteUrl } from './constants'
import { LzChainId, LzScanClient, OmniTx, OmniTxStatusProps, TransactionStatus } from './types'

export class OmniTransactionService {
  private defaultStatus = TransactionStatus.INFLIGHT

  constructor(private client: LzScanClient) {}

  async getRemoteTransactionStatuses(
    hash: string,
    remoteChains: number[],
  ): Promise<OmniTxStatusProps> {
    const { messages } = await this.client.getMessagesBySrcTxHash(hash)
    const deliveredMessages = messages.filter(
      (tx) => tx.dstTxHash && tx.status === MessageStatus.DELIVERED,
    ) as Required<Message>[]

    const outputMessages = await this.getOutputMessages(deliveredMessages)
    const transactions = await this.getTransactions(remoteChains, messages, outputMessages)

    return {
      transactions,
    }
  }

  private async createStatus(tx: Message) {
    if (!tx.dstTxHash) return this.defaultStatus

    if (tx.status === MessageStatus.FAILED) return TransactionStatus.FAILED

    return this.updateTransactionalStatuses(tx as Required<Message>)
  }

  private async updateTransactionalStatuses({
    dstTxHash,
    dstChainId,
    status,
  }: Required<Message>): Promise<TransactionStatus> {
    const { rpc, chain } = await getOmniRemoteUrl(dstChainId as LzChainId)
    const publicClient: PublicClient = createPublicClient({
      chain: chain,
      transport: http(rpc),
    })

    const { logs } = await publicClient.getTransactionReceipt({ hash: dstTxHash as Address })

    const isError =
      logs.length === 0 ||
      logs.some(({ topics }) => topics.some((topic) => errorTopics.includes(topic.toLowerCase())))

    const returnedStatus = isError
      ? TransactionStatus.FAILED
      : (status as unknown as TransactionStatus)

    return returnedStatus
  }

  private async getOutputMessages(deliveredMessages: Required<Message>[]): Promise<Message[]> {
    return Promise.all(
      deliveredMessages.map(async ({ dstTxHash }) => {
        const { messages } = await this.client.getMessagesBySrcTxHash(dstTxHash)
        return messages
      }),
    ).then((res) => res.flat())
  }

  private async getTransactions(
    remoteChains: number[],
    messages: Message[],
    outputMessages: Message[],
  ): Promise<OmniTx[]> {
    const transactions = await Promise.all(
      remoteChains.map(async (chain) => {
        const homeToRemote = messages.filter((msg) => msg.dstChainId === chain)
        const remoteToHome = outputMessages.find((msg) => msg.srcChainId === chain)
        const homeToRemoteHashes = homeToRemote.map((el) => ({
          hash: (el.dstTxHash as Address) ?? zeroAddress,
          dstChainId: el.dstChainId,
        }))
        const swapLogIncluded = await Promise.all(
          homeToRemoteHashes.map(async ({ hash, dstChainId }) => {
            const { rpc, chain } = await getOmniRemoteUrl(dstChainId as LzChainId)
            const publicClient: PublicClient = createPublicClient({
              chain: chain,
              transport: http(rpc),
            })

            if (hash === zeroAddress) {
              return false
            }

            const { logs } = await publicClient.getTransactionReceipt({ hash })
            const isSuccess =
              logs.length === 0 ||
              logs.some(({ topics }) =>
                topics.some(
                  (topic) =>
                    topic.toLocaleLowerCase() ===
                    '0xe9bded5f24a4168e4f3bf44e00298c993b22376aad8c58c7dda9718a54cbea82',
                ),
              )
            return isSuccess
          }),
        )

        const [homeStatuses, remoteStatuses] = await Promise.all([
          this.getStatuses(homeToRemote),
          this.getStatus(remoteToHome),
        ])

        return {
          escrowSwapsSuccess: swapLogIncluded.some((el) => el === true),
          chain,
          statuses: [...homeStatuses, remoteStatuses],
        }
      }),
    )

    return transactions
  }

  private async getStatuses(messages: Message[]): Promise<TransactionStatus[]> {
    return messages.length !== 0
      ? await Promise.all(messages.map((tx) => this.createStatus(tx)))
      : [this.defaultStatus]
  }

  private async getStatus(message: Message | undefined): Promise<TransactionStatus> {
    return message !== undefined ? await this.createStatus(message) : this.defaultStatus
  }
}
